/* Central HTTP client. All per-resource modules in src/api/ funnel through here.
 *
 * Features:
 * · Auth header injected from auth.js
 * · 401 → clearSession() and bubble ApiError(isAuth) so App.jsx redirects
 * · Idempotency-Key header for non-idempotent calls
 * · Network-error retry (GET only, once, 500ms backoff)
 * · Configurable timeout (default 12s) via AbortController
 * · Tracing: surfaces backend X-Trace-Id on every error
 */

import { config } from './config.js';
import { getToken, clearSession } from './auth.js';
import { ApiError } from './errors.js';
import { keyFor, uuid } from './idempotency.js';

const DEFAULT_TIMEOUT_MS = 12_000;
const RETRY_BACKOFF_MS = 500;

function joinUrl(path) {
  if (/^https?:/i.test(path)) return path;
  const base = config.API_BASE_URL.replace(/\/+$/, '');
  const rest = path.replace(/^\/+/, '');
  return `${base}/${rest}`;
}

async function parseResponse(res) {
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    try { return await res.json(); } catch { return null; }
  }
  const text = await res.text();
  return text || null;
}

async function fetchOnce(url, init, timeoutMs) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}

/**
 * Low-level request. Most callers should use the verb helpers below.
 *
 * opts:
 *   method, body, query, headers, timeoutMs
 *   idempotencyScope  → enables Idempotency-Key (deterministic per (scope, body))
 *   forceFreshKey     → if true, always generate a new key
 *   noAuth            → skip Authorization header (e.g. /auth/otp/request)
 *   retry             → override GET retry-once behaviour
 */
export async function request(path, opts = {}) {
  if (config.IS_MOCK) {
    throw new ApiError({ code: 'mock_no_endpoint', message: `No API_BASE_URL configured (called ${path}). Per-resource modules should be using their mock fallbacks.`, status: 0 });
  }

  const method = (opts.method || 'GET').toUpperCase();
  const url = (() => {
    const u = joinUrl(path);
    if (!opts.query) return u;
    const qp = new URLSearchParams();
    Object.entries(opts.query).forEach(([k, v]) => { if (v !== undefined && v !== null) qp.append(k, v); });
    const qs = qp.toString();
    return qs ? `${u}${u.includes('?') ? '&' : '?'}${qs}` : u;
  })();

  const headers = new Headers(opts.headers || {});
  headers.set('Accept', 'application/json');
  if (opts.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (!opts.noAuth) {
    const tok = getToken();
    if (tok) headers.set('Authorization', `Bearer ${tok}`);
  }
  if (opts.idempotencyScope) {
    const key = opts.forceFreshKey ? uuid() : keyFor(opts.idempotencyScope, opts.body ?? {});
    headers.set('Idempotency-Key', key);
  }

  const init = {
    method,
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    credentials: 'omit',
    mode: 'cors',
  };

  const shouldRetry = opts.retry ?? (method === 'GET');
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  let res;
  try {
    res = await fetchOnce(url, init, timeoutMs);
  } catch (e) {
    if (e?.name === 'AbortError') {
      if (shouldRetry) {
        await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
        res = await fetchOnce(url, init, timeoutMs);
      } else {
        throw ApiError.timeout();
      }
    } else if (shouldRetry) {
      await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS));
      try { res = await fetchOnce(url, init, timeoutMs); }
      catch (e2) { throw ApiError.network(e2); }
    } else {
      throw ApiError.network(e);
    }
  }

  const traceId = res.headers.get('x-trace-id') || null;

  if (res.status === 204) return null;

  const payload = await parseResponse(res);

  if (!res.ok) {
    const err = ApiError.fromResponse(res.status, payload, traceId);
    if (err.isAuth) clearSession();
    throw err;
  }

  return payload;
}

export const api = {
  get:  (path, opts)        => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts)  => request(path, { ...opts, method: 'POST',  body }),
  put:  (path, body, opts)  => request(path, { ...opts, method: 'PUT',   body }),
  patch:(path, body, opts)  => request(path, { ...opts, method: 'PATCH', body }),
  del:  (path, opts)        => request(path, { ...opts, method: 'DELETE' }),
};

/**
 * Helper for per-resource modules: run the real call if API_BASE_URL is set,
 * otherwise return the mock value (after a brief delay so loading UI gets a chance).
 *
 *   return mockOr(
 *     () => api.get('/branches'),
 *     () => D.branches.grocery,
 *   );
 */
export async function mockOr(realFn, mockFn, { mockDelayMs = 180 } = {}) {
  if (config.IS_MOCK) {
    if (mockDelayMs) await new Promise((r) => setTimeout(r, mockDelayMs));
    return mockFn();
  }
  return realFn();
}
